import { ArrowRight, ExternalLink, Monitor } from "lucide-react";
import type { ReactElement } from "react";

import { cn } from "@/lib/utils";
import { signal } from "@/motion/signals";

export function WorkspacePreviewCard({
  url,
  onViewPreview,
  enter = false,
}: {
  url: string;
  onViewPreview?: () => void;
  enter?: boolean;
}): ReactElement | null {
  const preview = safePreviewUrl(url);
  if (!preview) return null;

  return (
    <div
      role="group"
      aria-label="Updated preview"
      className={cn(
        "mt-3 overflow-hidden rounded-xl border border-[#d7e0dc] bg-[#f5f8f6] shadow-[0_5px_18px_rgba(42,65,59,0.06)]",
        enter && signal.confirm,
      )}
    >
      <div className="flex min-w-0 items-center gap-3 px-3 py-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#dce9e5] text-[#315d54]">
          <Monitor className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold leading-5 text-[#29312e]">
            Preview updated
          </p>
          <p className="truncate text-xs leading-5 text-[#727770]">
            {preview.host}
          </p>
        </div>
      </div>
      <div className="flex border-t border-[#dfe6e2] bg-white/70">
        {onViewPreview ? (
          <button
            type="button"
            onClick={onViewPreview}
            className="inline-flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#315d54] hover:bg-[#edf3f0] lg:hidden"
          >
            View preview
            <ArrowRight className="size-3" aria-hidden="true" />
          </button>
        ) : null}
        <a
          href={preview.href}
          target="_blank"
          rel="noreferrer noopener"
          referrerPolicy="no-referrer"
          className="inline-flex flex-1 items-center justify-center gap-1.5 border-l border-[#dfe6e2] px-3 py-2 text-xs font-semibold text-[#315d54] hover:bg-[#edf3f0] first:border-l-0"
          aria-label="Open preview in new tab"
        >
          Open
          <ExternalLink className="size-3" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

function safePreviewUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
}
